/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { createQuery } from '@/utils/createReactQuery';
import { getThirdPartyAxios } from '@/lib/axios/thirdPartyClient';
import { useFeatureFlag } from '@/features/globalConfig';
import { useLocale } from '@/hooks/useLocale';
import { type AxiosInstance } from 'axios';
import { type QueryClient } from '@tanstack/react-query';

export type EntitySchemaOptions = {
  entityType: 'location';
  id: string;
  language: string;
  yextURL: string;
  yextAPIKey: string;
  yextVersion: string;
};

export type EntitySchemaResponse = {
  response: unknown;
};

const getEntitySchema = async (options: EntitySchemaOptions): Promise<EntitySchemaResponse> => {
  const { yextURL, yextAPIKey, yextVersion, entityType, id, language } = options;

  if (!yextURL || !yextAPIKey || !yextVersion || !id || !language) {
    throw new Error('Missing required Yext entity schema parameters');
  }

  const url = `${yextURL}me/entityschema/${entityType}/${id}/${language}`;

  const response = await getThirdPartyAxios().get<EntitySchemaResponse>(url, {
    params: { v: yextVersion },
    headers: { 'api-key': yextAPIKey },
  });

  return response.data;
};

const { useData, prefetch } = createQuery<EntitySchemaResponse, EntitySchemaOptions>(
  'yextEntitySchema',
  async (options) => getEntitySchema(options),
);

export const useYextEntitySchema = (id: string | undefined) => {
  const locale = useLocale();
  const yextURL = useFeatureFlag('YEXT_URL') ?? '';
  const yextAPIKey = useFeatureFlag('YEXT_API_KEY') ?? '';
  const yextVersion = useFeatureFlag('YEXT_VERSION') ?? '';
  const language = (locale.split('-')[0] || 'en').toLowerCase();

  return useData({
    entityType: 'location',
    id: id ?? '',
    language,
    yextURL,
    yextAPIKey,
    yextVersion,
    enabled: Boolean(id && yextURL && yextAPIKey && yextVersion),
  });
};

export const prefetchYextEntitySchema = (
  queryClient: QueryClient,
  axios: AxiosInstance,
  options: EntitySchemaOptions,
) => prefetch(queryClient, options, axios);
